import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { FormField, form } from '@angular/forms/signals';
import { debounceTime, distinctUntilChanged, finalize, map, skip, Subscription } from 'rxjs';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import {
  AdminCreatorTaxService,
  TaxProfileStatus,
  TaxReadinessBucket,
  TaxReadinessRow,
  TaxReadinessStats,
} from '../../settings/services/admin-creator-tax.service';

interface BucketTab {
  id: TaxReadinessBucket | 'all';
  label: string;
  /** Short explanation of what the operator is looking at, and what to do about it. */
  hint: string;
}

/**
 * US tax (Form 1099-NEC) operations console.
 *
 * Deliberately **not** a filing console. Airwallex exposes no filing API — an operator
 * files in the Airwallex web app (Transfers → Tax Filing). This screen exists to support
 * the work either side of that: who is ready, who is blocked and why, what our ledger says
 * so it can be diffed against Airwallex's export, and recording what came back so the
 * creator app can show a real filing date.
 */
@Component({
  selector: 'admin-tax',
  imports: [FormField, CurrencyPipe, DatePipe],
  templateUrl: './tax.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Tax implements OnInit, OnDestroy {
  /** Matches the API's default page size; the US payee population is small. */
  private readonly pageSize = 50;

  /**
   * Bumped whenever the year / bucket / search context resets, so a slow response from a
   * previous context can't overwrite the current list. Same guard as the payouts tab.
   */
  private readonly listGeneration = signal(0);

  private subscriptions = new Subscription();

  private taxService = inject(AdminCreatorTaxService);
  public toastNotificationService = inject(ToastNotificationService);

  readonly tabs: BucketTab[] = [
    { id: 'all', label: 'All', hint: 'Every US payee with settled USD earnings this year.' },
    {
      id: 'ready_to_file',
      label: 'Ready to file',
      hint: 'Over threshold with a W-9 on file. File these in the Airwallex web app, then record the result below.',
    },
    {
      id: 'no_tax_form',
      label: 'Missing W-9',
      hint: 'Over threshold but no usable W-9. Airwallex cannot file for these — chase the creator.',
    },
    {
      id: 'filed',
      label: 'Filed',
      hint: 'Recorded as filed. These years are now unlocked in the creator app.',
    },
    {
      id: 'below_threshold',
      label: 'Below threshold',
      hint: 'Under this year’s federal floor. No 1099 is due; the creator gets an earnings summary instead.',
    },
  ];

  /** Default to the most recently closed year — the one that is actually filable. */
  readonly selectedYear = signal<number>(new Date().getUTCFullYear() - 1);
  readonly selectedTab = signal<TaxReadinessBucket | 'all'>('all');

  readonly rows = signal<TaxReadinessRow[] | undefined>(undefined);
  readonly totalCount = signal(0);
  readonly loadingMore = signal(false);
  readonly hasMore = signal(true);
  readonly pageNumber = signal(0);
  readonly stats = signal<TaxReadinessStats | undefined>(undefined);
  readonly loadError = signal<string | null>(null);

  searchQueryModel = signal({ query: '' });
  searchQueryForm = form(this.searchQueryModel);

  readonly searchTerm = computed(() => this.searchQueryModel().query.trim());

  /** Year options: the current (accruing) year plus the three Airwallex allows late filing for. */
  readonly yearOptions = computed(() => {
    const current = new Date().getUTCFullYear();
    return [current, current - 1, current - 2, current - 3];
  });

  readonly activeHint = computed(
    () => this.tabs.find((t) => t.id === this.selectedTab())?.hint ?? '',
  );

  /** Rows whose Airwallex linkage is broken — a filing-accuracy problem, not cosmetic. */
  readonly unlinkedCount = computed(
    () => (this.rows() ?? []).filter((row) => !row.externalIdentifierOk).length,
  );

  /** Rows where our ledger and Airwallex's reported figure disagree. */
  readonly mismatchCount = computed(
    () =>
      (this.rows() ?? []).filter(
        (row) => row.reportedAmountMinor !== null && row.reportedAmountMinor !== row.totalMinor,
      ).length,
  );

  constructor() {
    const searchSub = toObservable(this.searchQueryModel)
      .pipe(
        map((m) => m.query.trim()),
        debounceTime(300),
        distinctUntilChanged(),
        skip(1),
      )
      .subscribe(() => this.reload());
    this.subscriptions.add(searchSub);
  }

  ngOnInit(): void {
    this.reload();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  selectYear(year: number): void {
    if (year === this.selectedYear()) return;
    this.selectedYear.set(year);
    this.reload();
  }

  selectTab(tab: TaxReadinessBucket | 'all'): void {
    if (tab === this.selectedTab()) return;
    this.selectedTab.set(tab);
    this.reload();
  }

  /** Resets paging and refetches both the list and the tab counts. */
  reload(): void {
    this.pageNumber.set(0);
    this.rows.set(undefined);
    this.hasMore.set(true);
    this.loadError.set(null);
    this.listGeneration.update((g) => g + 1);

    this.loadStats();
    this.loadRows(0, false, this.listGeneration());
  }

  private loadStats(): void {
    const generation = this.listGeneration();
    const sub = this.taxService.getStats(this.selectedYear()).subscribe({
      next: (stats) => {
        if (generation !== this.listGeneration()) return;
        this.stats.set(stats);
      },
      error: () => this.stats.set(undefined),
    });
    this.subscriptions.add(sub);
  }

  private loadRows(page: number, append: boolean, generation: number): void {
    if (append) {
      if (this.loadingMore()) return;
      this.loadingMore.set(true);
    }

    const bucket = this.selectedTab();
    const term = this.searchTerm();

    const sub = this.taxService
      .getReadiness({
        taxYear: this.selectedYear(),
        bucket: bucket === 'all' ? undefined : bucket,
        searchTerm: term.length > 0 ? term : undefined,
        skip: page * this.pageSize,
        take: this.pageSize,
      })
      .pipe(finalize(() => append && this.loadingMore.set(false)))
      .subscribe({
        next: (response) => {
          if (generation !== this.listGeneration()) return;

          const chunk = response.rows ?? [];
          if (append && this.rows()) {
            const existing = this.rows()!;
            this.rows.set([
              ...existing,
              ...chunk.filter((r) => !existing.some((e) => e.blazeFirebaseUid === r.blazeFirebaseUid)),
            ]);
          } else {
            this.rows.set(chunk);
          }

          this.totalCount.set(response.totalCount ?? chunk.length);
          this.hasMore.set((this.rows() ?? []).length < (response.totalCount ?? 0));
          this.pageNumber.set(page);
        },
        error: () => {
          if (generation !== this.listGeneration()) return;
          this.rows.set([]);
          this.loadError.set("Couldn't load tax readiness. Please try again.");
        },
      });

    this.subscriptions.add(sub);
  }

  onTableScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (el.scrollHeight - el.scrollTop - el.clientHeight > 120) return;
    if (!this.hasMore() || this.loadingMore()) return;
    this.loadRows(this.pageNumber() + 1, true, this.listGeneration());
  }

  /**
   * Marks a creator-year filed. Confirmed first because it is the write that makes a 1099
   * visible to the creator — doing it before Airwallex has actually filed would show
   * someone a form that does not exist.
   */
  async markFiled(row: TaxReadinessRow): Promise<void> {
    const confirmed = await this.toastNotificationService.confirm({
      title: `Mark ${row.creatorName} as filed?`,
      message:
        `This unlocks their ${this.selectedYear()} 1099-NEC in the SHOUT app. ` +
        `Only do this once Airwallex has actually filed it.`,
      type: 'warning',
      icon: 'warning',
    });
    if (!confirmed) return;

    const sub = this.taxService
      .recordFilings(this.selectedYear(), [
        {
          blazeFirebaseUid: row.blazeFirebaseUid,
          status: 'filed',
          filedDate: new Date().toISOString(),
          reportedAmountMinor: row.totalMinor,
        },
      ])
      .subscribe({
        next: (result) => {
          if (result.mismatches.length > 0) {
            this.toastNotificationService.alert({
              title: 'Recorded, but amounts disagree',
              message: result.mismatches.join('; '),
              type: 'warning',
              icon: 'warning',
            });
          } else {
            this.toastNotificationService.alert({
              title: 'Recorded as filed',
              message: `${row.creatorName}'s ${this.selectedYear()} 1099-NEC is now available in the app.`,
              type: 'success',
              icon: 'success',
            });
          }
          this.reload();
        },
        error: () =>
          this.toastNotificationService.alert({
            title: "Couldn't record that",
            message: 'Please try again.',
            type: 'error',
          icon: 'error',
          }),
      });

    this.subscriptions.add(sub);
  }

  /** Re-checks the Airwallex beneficiary linkage for one creator. */
  syncBeneficiary(row: TaxReadinessRow): void {
    const sub = this.taxService.syncBeneficiary(row.blazeFirebaseUid).subscribe({
      next: (result) => {
        this.toastNotificationService.alert({
          title: result.ok ? 'Linkage confirmed' : 'Linkage missing',
          message: result.ok
            ? 'Airwallex holds the right external identifier for this creator.'
            : 'Airwallex does not hold the right external identifier. Their transfers may not be ' +
              'associated with their W-9 — ask them to re-save their bank details, and note that ' +
              'Airwallex only applies it to future transfers.',
          type: result.ok ? 'success' : 'warning',
        icon: result.ok ? 'success' : 'warning',
        });
        this.reload();
      },
      error: () =>
        this.toastNotificationService.alert({
          title: "Couldn't check that",
          message: 'Please try again.',
          type: 'error',
        icon: 'error',
        }),
    });
    this.subscriptions.add(sub);
  }

  /**
   * Records the TIN-matching outcome an operator read in the Airwallex web app.
   * Confirmed first because `invalid` locks the creator out of their own documents.
   */
  async setProfileStatus(row: TaxReadinessRow, status: TaxProfileStatus): Promise<void> {
    const confirmed = await this.toastNotificationService.confirm({
      title: status === 'invalid' ? `Mark ${row.creatorName}'s W-9 invalid?` : `Mark ${row.creatorName} verified?`,
      message:
        status === 'invalid'
          ? 'This locks their tax years and asks them to resubmit their tax form.'
          : 'Only do this once Airwallex shows their TIN as matched.',
      type: 'warning',
      icon: 'warning',
    });
    if (!confirmed) return;

    const sub = this.taxService.setProfileStatus(row.blazeFirebaseUid, status).subscribe({
      next: () => {
        this.toastNotificationService.alert({
          title: 'Tax profile updated',
          message: `${row.creatorName} is now ${status}.`,
          type: 'success',
          icon: 'success',
        });
        this.reload();
      },
      error: () =>
        this.toastNotificationService.alert({
          title: "Couldn't update that",
          message: 'Please try again.',
          type: 'error',
          icon: 'error',
        }),
    });
    this.subscriptions.add(sub);
  }

  downloadReadinessCsv(): void {
    const year = this.selectedYear();
    const sub = this.taxService.exportReadinessCsv(year).subscribe({
      next: (csv) => this.saveCsv(csv, `tax-readiness-${year}.csv`),
      error: () =>
        this.toastNotificationService.alert({
          title: "Couldn't export",
          message: 'Please try again.',
          type: 'error',
        icon: 'error',
        }),
    });
    this.subscriptions.add(sub);
  }

  downloadPostalCsv(): void {
    const year = this.selectedYear();
    const sub = this.taxService.exportPostalCsv(year).subscribe({
      next: (csv) => this.saveCsv(csv, `tax-postal-delivery-${year}.csv`),
      error: () =>
        this.toastNotificationService.alert({
          title: "Couldn't export",
          message: 'Please try again.',
          type: 'error',
        icon: 'error',
        }),
    });
    this.subscriptions.add(sub);
  }

  private saveCsv(csv: string, fileName: string): void {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
  }

  bucketLabel(bucket: TaxReadinessBucket): string {
    return this.tabs.find((t) => t.id === bucket)?.label ?? bucket;
  }

  bucketClass(bucket: TaxReadinessBucket): string {
    switch (bucket) {
      case 'ready_to_file':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300';
      case 'no_tax_form':
        return 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300';
      case 'filed':
        return 'bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-300';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-white/10 dark:text-gray-300';
    }
  }

  getTabClass(tab: TaxReadinessBucket | 'all'): string {
    return this.selectedTab() === tab
      ? 'bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-gray-200'
      : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200';
  }

  getYearClass(year: number): string {
    return this.selectedYear() === year
      ? 'bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-gray-200'
      : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200';
  }

  statFor(bucket: TaxReadinessBucket | 'all'): number {
    const stats = this.stats();
    if (!stats) return 0;
    if (bucket === 'all') {
      return stats.no_tax_form + stats.ready_to_file + stats.filed + stats.below_threshold;
    }
    return stats[bucket] ?? 0;
  }

  /** Minor units -> major, for the CurrencyPipe. */
  toMajor(minor: number | null): number {
    return (minor ?? 0) / 100;
  }

  hasMismatch(row: TaxReadinessRow): boolean {
    return row.reportedAmountMinor !== null && row.reportedAmountMinor !== row.totalMinor;
  }

  canMarkFiled(row: TaxReadinessRow): boolean {
    return row.bucket === 'ready_to_file';
  }
}
